/**
 * Plain-English text for the product quick view (PR 10, plan I5). Pure, so
 * the drawer's sentences are pinned by quickView.test.tsx — every line an
 * employee reads, no internal field names.
 */
import type { OpenPoLine, ProductQuickView, ProductSnapshotWindow } from '../../ops/lib/api'
import { fmtInt } from '../../lib/format'
import { fmtMonthDay, parseYMD } from '../../lib/format/date'

const MONTH_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' })

/** "Dec 2035" for a YYYY-MM-DD, or '—'. */
export function monthYear(ymd: string | null | undefined): string {
  const d = parseYMD(ymd)
  return d ? MONTH_YEAR.format(d) : '—'
}

/** "Last 30 complete days (Sep 14 – Oct 13)". */
export function windowCaption(w: ProductSnapshotWindow | null | undefined): string {
  if (!w) return 'Last 30 complete days'
  return `Last ${w.days} complete days (${fmtMonthDay(w.from)} – ${fmtMonthDay(w.to)})`
}

/** "919 units · lasts 3,240 days (runs out Dec 2035)". */
export function stockHeadline(q: Pick<ProductQuickView, 'stock_units' | 'days_of_cover' | 'runs_out_on'>): string {
  if (q.stock_units == null) return 'No stock on record'
  const units = `${fmtInt(q.stock_units)} ${q.stock_units === 1 ? 'unit' : 'units'}`
  if (q.days_of_cover == null) return q.stock_units > 0 ? `${units} · not selling, so it does not run out` : units
  const days = `lasts ${fmtInt(q.days_of_cover)} ${Math.round(q.days_of_cover) === 1 ? 'day' : 'days'}`
  return q.runs_out_on ? `${units} · ${days} (runs out ${monthYear(q.runs_out_on)})` : `${units} · ${days}`
}

/** The on-order line's label. Pooled stock (Restock's Total Stock) counts
 *  what is on order; an Amazon tab's stock is that marketplace's FBA only. */
export function onOrderLabel(q: Pick<ProductQuickView, 'days_of_cover_basis'>): string {
  return q.days_of_cover_basis === 'fba_this_marketplace'
    ? 'On order (not counted above)'
    : 'On order from suppliers'
}

/** The PO that lands next: the one whose ETA is the earliest (the Restock
 *  row's `next_po_eta`), else the first open line. */
export function nextPo(q: Pick<ProductQuickView, 'open_po_lines' | 'next_po_eta'>): OpenPoLine | null {
  const lines = q.open_po_lines ?? []
  if (lines.length === 0) return null
  const byEta = q.next_po_eta ? lines.find((l) => l.eta === q.next_po_eta) : undefined
  if (byEta) return byEta
  const dated = lines.filter((l) => l.eta).sort((a, b) => (a.eta as string).localeCompare(b.eta as string))
  return dated[0] ?? lines[0]
}

/** "PO 1759 · ETA Nov 2 (late)" / "PO 1759 · no ETA yet" / "No open purchase orders". */
export function nextPoText(q: Pick<ProductQuickView, 'open_po_lines' | 'next_po_eta' | 'next_po_overdue'>): string {
  const po = nextPo(q)
  if (!po) return 'No open purchase orders'
  if (!po.eta) return `PO ${po.po_number} · no ETA yet`
  const late = q.next_po_overdue && po.eta === q.next_po_eta
  return `PO ${po.po_number} · ETA ${fmtMonthDay(po.eta)}${late ? ' (late)' : ''}`
}

const POOLS: [string, string][] = [
  ['warehouse', 'Warehouse'],
  ['other_wh', 'Other warehouses'],
  ['wfs', 'Walmart WFS'],
  ['fba_on_hand', 'FBA on hand'],
  ['fba_inbound', 'Inbound to Amazon'],
]

/** The stock pools with something in them, in a fixed order. */
export function stockPools(breakdown: Record<string, number> | null | undefined): { label: string; units: number }[] {
  if (!breakdown) return []
  return POOLS
    .filter(([k]) => (breakdown[k] ?? 0) > 0)
    .map(([k, label]) => ({ label, units: breakdown[k] }))
}

/** The Marketplace Hub's Products view, filtered to one product. */
export function hubProductsLink(uid: string): string {
  return `/inventory/marketplaces?tab=products&uid=${encodeURIComponent(uid)}`
}

/** True when a link is an in-app path (a router Link) rather than an
 *  external URL (an <a target=_blank>). */
export function isInAppLink(href: string): boolean {
  return href.startsWith('/')
}
